import { useEffect, useState } from "react";

interface Estimate {
  usageMb: number;
  quotaMb: number;
  pct: number;
}

/**
 * Reads `navigator.storage.estimate()` once on mount and again every 60 s.
 * Renders an unobtrusive line-item showing how much PWA storage is used.
 * Useful for verifying that the precache (~35 MB of source docs) actually
 * landed.
 *
 * Returns null when the API isn't available (older browsers).
 */
export function StorageEstimate({
  className = "",
}: {
  className?: string;
}) {
  const [est, setEst] = useState<Estimate | null>(null);

  useEffect(() => {
    if (!navigator.storage?.estimate) return;
    let cancelled = false;
    async function tick() {
      try {
        const r = await navigator.storage.estimate();
        if (cancelled) return;
        const usageMb = Math.round((r.usage ?? 0) / 1024 / 1024);
        const quotaMb = Math.round((r.quota ?? 0) / 1024 / 1024);
        const pct = quotaMb > 0 ? (usageMb / quotaMb) * 100 : 0;
        setEst({ usageMb, quotaMb, pct });
      } catch {
        /* ignore */
      }
    }
    void tick();
    const id = setInterval(tick, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (!est) return null;

  // Format quota as GB if large
  const quotaDisplay =
    est.quotaMb >= 1024
      ? `${(est.quotaMb / 1024).toFixed(1)} GB`
      : `${est.quotaMb} MB`;

  return (
    <div
      className={`text-2xs text-zinc-500 tabular ${className}`}
      title={`${est.usageMb} MB van ${est.quotaMb} MB browser-quota gebruikt`}
    >
      Cache: <span className="text-zinc-300">{est.usageMb} MB</span>
      <span className="text-zinc-600"> / {quotaDisplay}</span>
    </div>
  );
}
